import React from "react";
import { orders as ordersApi } from "shared/api";

/**
 * Tax for the admin "New Order" cart, from the BFF (POST /v1/orders/tax), which
 * prices and taxes the lines exactly as placing the order will. Re-fetched,
 * debounced, whenever the lines or the destination change. `estimate` is null
 * until the first answer, and its tax/total are null when the BFF can't work
 * them out — callers show "—" then, never a guessed figure.
 */
export default function useOrderTaxEstimate({
  items,
  iboId,
  shipToAddressId,
  tempAddress,
}) {
  const [estimate, setEstimate] = React.useState(null);

  const lines = React.useMemo(
    () =>
      (items || []).map((item) => ({
        sku: item.sku ?? null,
        // Same identity placeOrder sends: a duplicated product shares its
        // original's sku, and the BFF picks the price override by productId,
        // so without it the preview would price the original instead.
        productId: item.productId ?? null,
        quantity: item.qty || 1,
        unitPriceCents: Math.round(
          (Number(item.price ?? item.retailPrice ?? item.pm ?? 0) || 0) * 100
        ),
      })),
    [items]
  );
  const key = JSON.stringify({ lines, iboId, shipToAddressId, tempAddress });

  React.useEffect(() => {
    if (!iboId || !lines.length) {
      setEstimate(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      ordersApi
        .estimateTax({
          iboId,
          shipToAddressId: shipToAddressId ?? null,
          tempAddress: tempAddress ?? null,
          items: lines,
        })
        .then((res) => {
          if (!cancelled) setEstimate(res || null);
        })
        .catch(() => {
          if (!cancelled) setEstimate(null);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `key` captures every input by value, so re-renders that rebuild equal
    // arrays don't re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return estimate;
}
